import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Lock, 
  Unlock, 
  Plus, 
  X, 
  Edit3, 
  Trash2, 
  Shield, 
  Key,
  Eye,
  EyeOff,
  Clock,
  BookOpen,
  Save,
  AlertTriangle,
  ChevronLeft,
  Image,
  Upload,
  Download,
  RefreshCw,
  Mail,
  Check,
  Copy,
  Stethoscope,
  Loader2
} from 'lucide-react';

const MOOD_OPTIONS = [
  { value: 'peaceful', label: '😌 Peaceful', color: 'bg-green-500/20', textColor: '#22C55E' },
  { value: 'anxious', label: '😰 Anxious', color: 'bg-yellow-500/20', textColor: '#EAB308' },
  { value: 'sad', label: '😢 Sad', color: 'bg-blue-500/20', textColor: '#3B82F6' },
  { value: 'angry', label: '😠 Angry', color: 'bg-red-500/20', textColor: '#EF4444' },
  { value: 'hopeful', label: '🌟 Hopeful', color: 'bg-purple-500/20', textColor: '#A855F7' },
  { value: 'confused', label: '😕 Confused', color: 'bg-orange-500/20', textColor: '#FF8C00' }
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const PrivateVault = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [resetPinStep, setResetPinStep] = useState('request'); // 'request', 'verify'
  const [resetToken, setResetToken] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const imageInputRef = useRef(null);
  
  // Doctor access code state
  const [doctorAccessCode, setDoctorAccessCode] = useState(null);
  const [generatingDoctorCode, setGeneratingDoctorCode] = useState(false);

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get('reset_token');
    if (token) {
      setResetToken(token);
      setResetPinStep('verify');
      setShowResetPinModal(true);
      // Clear the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Entry form state
  const [entryForm, setEntryForm] = useState({
    title: '',
    content: '',
    tags: '',
    mood: '',
    images: []
  });

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (entryForm.images.length + files.length > 5) {
      toast.error('Maximum 5 images per entry');
      return;
    }

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      if (file.size > 12 * 1024 * 1024) {
        toast.error('Image too large. Maximum 12MB per image');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setEntryForm(prev => ({
          ...prev,
          images: [...prev.images, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setEntryForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const fetchVaultStatus = useCallback(async (skipLockCheck = false) => {
    try {
      setLoading(true);
      const response = await api.get('/vault/status');
      
      // If we're told to skip lock check (after unlock), only update non-lock status
      if (skipLockCheck) {
        setStatus(prev => ({
          ...response.data,
          is_locked: prev?.is_locked ?? response.data.is_locked
        }));
      } else {
        setStatus(response.data);
      }
      
      // Only fetch entries if vault is enabled and unlocked
      if (response.data.is_enabled && !response.data.is_locked && !skipLockCheck) {
        fetchEntries();
      }
    } catch (error) {
      console.error('Failed to fetch vault status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await api.get('/vault/entries');
      setEntries(response.data);
      return true;
    } catch (error) {
      if (error.response?.status === 403) {
        // Only set locked if we weren't just unlocked (avoid race condition)
        // Check if we have a valid session expiry set
        if (!sessionExpiry || new Date() > new Date(sessionExpiry)) {
          setStatus(prev => ({ ...prev, is_locked: true }));
          setSessionExpiry(null);
        }
      }
      return false;
    }
  };

  useEffect(() => {
    fetchVaultStatus();
  }, [fetchVaultStatus]);

  useEffect(() => {
    // Auto-lock timer
    if (sessionExpiry) {
      const timer = setInterval(() => {
        if (new Date() > new Date(sessionExpiry)) {
          setStatus(prev => ({ ...prev, is_locked: true }));
          setEntries([]);
          setSessionExpiry(null);
          toast.info('Vault auto-locked for security');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [sessionExpiry]);

  const handleSetupVault = async (e) => {
    e.preventDefault();
    if (pin.length < 4 || pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/vault/setup', { pin, biometric_enabled: false });
      toast.success('Private Vault enabled!');
      setShowSetupModal(false);
      setPin('');
      setConfirmPin('');
      fetchVaultStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up vault');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlockVault = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const response = await api.post('/vault/unlock', { pin });
      
      // Set unlocked state BEFORE closing modal
      const expiresIn = response.data.expires_in || 900;
      setSessionExpiry(new Date(Date.now() + expiresIn * 1000));
      setStatus(prev => ({ ...prev, is_locked: false }));
      
      toast.success('Vault unlocked');
      setShowUnlockModal(false);
      setPin('');
      
      // Fetch entries after state is updated
      await fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock vault');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLockVault = async () => {
    try {
      await api.post('/vault/lock');
      setStatus(prev => ({ ...prev, is_locked: true }));
      setEntries([]);
      setSessionExpiry(null);
      toast.info('Vault locked');
    } catch (error) {
      toast.error('Failed to lock vault');
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.title.trim() || !entryForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setSubmitting(true);
      const tags = entryForm.tags.split(',').map(t => t.trim()).filter(t => t);
      
      if (selectedEntry) {
        // Update existing entry
        await api.put(`/vault/entries/${selectedEntry.id}`, {
          title: entryForm.title,
          content: entryForm.content,
          tags,
          mood: entryForm.mood || null,
          images: entryForm.images
        });
        toast.success('Entry updated');
      } else {
        // Create new entry
        await api.post('/vault/entries', {
          title: entryForm.title,
          content: entryForm.content,
          tags,
          mood: entryForm.mood || null,
          images: entryForm.images
        });
        toast.success('Entry saved to vault');
      }
      
      setShowEntryModal(false);
      setSelectedEntry(null);
      setEntryForm({ title: '', content: '', tags: '', mood: '', images: [] });
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) return;
    
    try {
      await api.delete(`/vault/entries/${entryId}`);
      toast.success('Entry deleted');
      setEntries(entries.filter(e => e.id !== entryId));
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const openEditModal = (entry) => {
    setSelectedEntry(entry);
    setEntryForm({
      title: entry.title,
      content: entry.content,
      tags: entry.tags.join(', '),
      mood: entry.mood || '',
      images: entry.images || []
    });
    setShowEntryModal(true);
  };

  const openNewEntryModal = () => {
    setSelectedEntry(null);
    setEntryForm({ title: '', content: '', tags: '', mood: '', images: [] });
    setShowEntryModal(true);
  };

  // PIN Reset handlers
  const handleRequestPinReset = async () => {
    try {
      setSubmitting(true);
      await api.post('/vault/request-pin-reset');
      toast.success('Verification email sent! Check your inbox.');
      setResetPinStep('waiting');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyPinReset = async (e) => {
    e.preventDefault();
    
    if (newPin !== confirmNewPin) {
      toast.error('PINs do not match');
      return;
    }
    
    if (!/^\d{4,6}$/.test(newPin)) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post('/vault/verify-pin-reset', {
        token: resetToken,
        new_pin: newPin
      });
      toast.success('PIN reset successful! Your vault has been reset.');
      setShowResetPinModal(false);
      setResetPinStep('request');
      setResetToken('');
      setNewPin('');
      setConfirmNewPin('');
      fetchVaultStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    } finally {
      setSubmitting(false);
    }
  };

  // PDF Download handler
  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get('/vault/download-pdf', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${user?.full_name?.replace(/\s+/g, '_') || 'My'}_Private_Journal.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Journal downloaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download journal');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Generate doctor access code
  const handleGenerateDoctorAccessCode = async () => {
    setGeneratingDoctorCode(true);
    try {
      const response = await api.post('/doctor-portal/patient/vault/generate-doctor-pin', { expires_in_hours: 24 });
      setDoctorAccessCode(response.data);
      toast.success('Doctor access code generated! Share it with your doctor.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate access code');
    } finally {
      setGeneratingDoctorCode(false);
    }
  };

  // Copy doctor access code
  const copyDoctorCode = async () => {
    if (!doctorAccessCode?.pin) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(doctorAccessCode.pin);
        toast.success('Code copied to clipboard!');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = doctorAccessCode.pin;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Code copied to clipboard!');
      }
    } catch (err) {
      toast.error('Failed to copy. Code: ' + doctorAccessCode.pin);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="private-vault-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-gradient-to-r from-violet-500/20 to-purple-500/20">
              <Shield className="w-8 h-8 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Private Mental Vault</h1>
              <p className="text-muted-foreground">Your secure, encrypted space for private thoughts</p>
            </div>
          </div>
        </div>

        {!status?.is_enabled ? (
          // Setup Prompt
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <Lock className="w-16 h-16 text-violet-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Enable Your Private Vault</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create a PIN-protected space for your most private thoughts. 
              All entries are encrypted and stored securely on your device.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6 max-w-md mx-auto">
              <AlertTriangle className="w-5 h-5 text-yellow-400 inline mr-2" />
              <span className="text-sm text-yellow-400">
                If you forget your PIN, your vault data cannot be recovered.
              </span>
            </div>
            <button
              onClick={() => setShowSetupModal(true)}
              className="px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-opacity font-medium"
              data-testid="enable-vault-button"
            >
              Enable Private Vault
            </button>
          </div>
        ) : status?.is_locked ? (
          // Locked State
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <Lock className="w-16 h-16 text-violet-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Vault is Locked</h2>
            <p className="text-muted-foreground mb-6">
              Enter your PIN to access your private entries
            </p>
            <button
              onClick={() => setShowUnlockModal(true)}
              className="px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-opacity font-medium"
              data-testid="unlock-vault-button"
            >
              <Key className="w-5 h-5 inline mr-2" />
              Unlock Vault
            </button>
            <p className="text-xs text-muted-foreground mt-4">
              {status.entry_count} encrypted {status.entry_count === 1 ? 'entry' : 'entries'} stored
            </p>
            <button
              onClick={() => {
                setResetPinStep('request');
                setShowResetPinModal(true);
              }}
              className="mt-4 text-sm text-violet-400 hover:text-violet-300 transition-colors underline"
              data-testid="reset-pin-link"
            >
              <RefreshCw className="w-3 h-3 inline mr-1" />
              Reset PIN
            </button>
          </div>
        ) : (
          // Unlocked State - Show Entries
          <>
            {/* Status Bar */}
            <div className="flex items-center justify-between mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
              <div className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Vault Unlocked</span>
                {sessionExpiry && (
                  <span className="text-xs text-muted-foreground ml-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Auto-locks in {Math.max(0, Math.floor((new Date(sessionExpiry) - new Date()) / 60000))} min
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={openNewEntryModal}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors"
                  data-testid="new-entry-button"
                >
                  <Plus className="w-4 h-4" />
                  New Entry
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || entries.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                  data-testid="download-journal-button"
                >
                  {downloadingPdf ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download PDF
                </button>
                <button
                  onClick={handleLockVault}
                  className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors"
                  data-testid="lock-vault-button"
                >
                  <Lock className="w-4 h-4" />
                  Lock
                </button>
              </div>
            </div>

            {/* Doctor Access Code Section */}
            <div className="mb-6 p-4 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-teal-500/20">
                  <Stethoscope className="w-5 h-5 text-teal-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">Share with Your Doctor</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate a temporary 6-digit code to allow your doctor to view your vault entries
                  </p>
                  
                  {doctorAccessCode ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div 
                          onClick={copyDoctorCode}
                          className="flex-1 px-4 py-2.5 bg-teal-500/10 border border-teal-500/30 rounded-xl text-teal-300 font-mono text-xl tracking-widest text-center cursor-pointer hover:bg-teal-500/20 transition-colors"
                        >
                          {doctorAccessCode.pin}
                        </div>
                        <button
                          onClick={copyDoctorCode}
                          className="p-2.5 bg-teal-500/20 text-teal-400 rounded-xl hover:bg-teal-500/30 transition-colors"
                          title="Copy code"
                          data-testid="copy-doctor-code-btn"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Expires: {new Date(doctorAccessCode.expires_at).toLocaleString()}
                        </p>
                        <button
                          onClick={handleGenerateDoctorAccessCode}
                          disabled={generatingDoctorCode}
                          className="text-xs text-teal-400 hover:text-teal-300 underline"
                        >
                          Generate new code
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateDoctorAccessCode}
                      disabled={generatingDoctorCode}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
                      data-testid="generate-doctor-code-btn"
                    >
                      {generatingDoctorCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Key className="w-4 h-4" />
                      )}
                      Generate Doctor Access Code
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Entries List */}
            {entries.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-12 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Your vault is empty</p>
                <button
                  onClick={openNewEntryModal}
                  className="px-6 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors"
                >
                  Write Your First Entry
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {entries.map(entry => {
                  const moodOption = MOOD_OPTIONS.find(m => m.value === entry.mood);
                  return (
                    <div
                      key={entry.id}
                      className="bg-card rounded-xl border border-border p-5 hover:border-violet-500/50 transition-colors"
                      data-testid={`vault-entry-${entry.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-foreground text-lg">{entry.title}</h3>
                          <p className="text-xs text-muted-foreground">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(entry.created_at)}
                            {entry.updated_at && ' (edited)'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {moodOption && (
                            <span className={`px-2 py-1 rounded-full text-xs ${moodOption.color}`}>
                              {moodOption.label}
                            </span>
                          )}
                          <button
                            onClick={() => openEditModal(entry)}
                            className="p-2 text-muted-foreground hover:text-violet-400 transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap line-clamp-4">{entry.content}</p>
                      
                      {/* Display Images */}
                      {entry.images && entry.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {entry.images.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Entry image ${idx + 1}`}
                              className="w-24 h-24 object-cover rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => window.open(img, '_blank')}
                            />
                          ))}
                        </div>
                      )}
                      
                      {entry.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {entry.tags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-muted rounded-lg text-xs text-muted-foreground">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="setup-modal">
          <div className="bg-card rounded-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Set Up Your Vault</h2>
                <button onClick={() => setShowSetupModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSetupVault} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Create PIN (4-6 digits)</label>
                <div className="relative">
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-violet-500"
                    maxLength={6}
                    data-testid="setup-pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Confirm PIN</label>
                <input
                  type={showPin ? 'text' : 'password'}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-violet-500"
                  maxLength={6}
                  data-testid="confirm-pin-input"
                />
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                <AlertTriangle className="w-5 h-5 text-yellow-400 inline mr-2" />
                <span className="text-sm text-yellow-400">
                  Remember this PIN! If forgotten, your vault data cannot be recovered.
                </span>
              </div>

              <button
                type="submit"
                disabled={submitting || pin.length < 4 || pin !== confirmPin}
                className="w-full py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="submit-setup-button"
              >
                {submitting ? 'Setting up...' : 'Enable Vault'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="unlock-modal">
          <div className="bg-card rounded-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Unlock Vault</h2>
                <button onClick={() => setShowUnlockModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleUnlockVault} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Enter PIN</label>
                <div className="relative">
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-violet-500"
                    maxLength={6}
                    autoFocus
                    data-testid="unlock-pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || pin.length < 4}
                className="w-full py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="submit-unlock-button"
              >
                {submitting ? 'Unlocking...' : 'Unlock'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Entry Modal */}
      {showEntryModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="entry-modal">
          <div className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">
                  {selectedEntry ? 'Edit Entry' : 'New Entry'}
                </h2>
                <button 
                  onClick={() => { setShowEntryModal(false); setSelectedEntry(null); }} 
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateEntry} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Title</label>
                <input
                  type="text"
                  value={entryForm.title}
                  onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })}
                  placeholder="Give this entry a title..."
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                  required
                  data-testid="entry-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">How are you feeling?</label>
                <div className="flex flex-wrap gap-2">
                  {MOOD_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setEntryForm({ ...entryForm, mood: entryForm.mood === option.value ? '' : option.value })}
                      className={`px-3 py-2 rounded-lg text-sm transition-all ${
                        entryForm.mood === option.value 
                          ? option.color + ' ring-2 ring-offset-2 ring-offset-card ring-violet-500'
                          : 'bg-muted text-muted-foreground hover:bg-muted/70'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Your Thoughts</label>
                <textarea
                  value={entryForm.content}
                  onChange={(e) => setEntryForm({ ...entryForm, content: e.target.value })}
                  placeholder="Write freely... This is your safe space."
                  rows={8}
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  required
                  data-testid="entry-content-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Tags (optional)</label>
                <input
                  type="text"
                  value={entryForm.tags}
                  onChange={(e) => setEntryForm({ ...entryForm, tags: e.target.value })}
                  placeholder="anxiety, work, family (comma separated)"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="entry-tags-input"
                />
              </div>

              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Add Images (optional, max 5)
                </label>
                <div className="space-y-3">
                  {/* Image Previews */}
                  {entryForm.images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {entryForm.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img}
                            alt={`Upload ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {entryForm.images.length < 5 && (
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-muted border border-dashed border-border rounded-xl text-muted-foreground hover:text-foreground hover:border-violet-500 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Add Image</span>
                    </button>
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="vault-image-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>This entry will be encrypted and stored securely</span>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-entry-button"
              >
                <Save className="w-5 h-5" />
                {submitting ? 'Saving...' : 'Save Entry'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Reset PIN Modal */}
      {showResetPinModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-foreground">
                {resetPinStep === 'verify' ? 'Set New PIN' : 'Reset Your PIN'}
              </h3>
              <button
                onClick={() => {
                  setShowResetPinModal(false);
                  setResetPinStep('request');
                  setNewPin('');
                  setConfirmNewPin('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {resetPinStep === 'request' && (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 inline mr-2" />
                  <span className="text-sm text-yellow-400">
                    <strong>Warning:</strong> Resetting your PIN will permanently delete all existing vault entries. This cannot be undone.
                  </span>
                </div>
                
                <p className="text-muted-foreground text-sm">
                  A verification email will be sent to your registered email address. Click the link in the email to set a new PIN.
                </p>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetPinModal(false)}
                    className="flex-1 px-4 py-2 bg-muted text-foreground rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestPinReset}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-violet-500 text-white rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="send-reset-email-button"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    Send Verification Email
                  </button>
                </div>
              </div>
            )}

            {resetPinStep === 'waiting' && (
              <div className="text-center py-4">
                <Mail className="w-12 h-12 text-violet-400 mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">Check Your Email</p>
                <p className="text-muted-foreground text-sm">
                  We've sent a verification link to your email. Click the link to continue with PIN reset.
                </p>
                <button
                  onClick={() => setShowResetPinModal(false)}
                  className="mt-4 px-6 py-2 bg-muted text-foreground rounded-xl"
                >
                  Close
                </button>
              </div>
            )}

            {resetPinStep === 'verify' && (
              <form onSubmit={handleVerifyPinReset} className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <Check className="w-5 h-5 text-green-400 inline mr-2" />
                  <span className="text-sm text-green-400">
                    Email verified! Now set your new PIN.
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">New PIN (4-6 digits)</label>
                  <input
                    type="password"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter new PIN"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 text-center tracking-[1em]"
                    maxLength={6}
                    required
                    data-testid="new-pin-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Confirm New PIN</label>
                  <input
                    type="password"
                    value={confirmNewPin}
                    onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Confirm new PIN"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 text-center tracking-[1em]"
                    maxLength={6}
                    required
                    data-testid="confirm-new-pin-input"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowResetPinModal(false)}
                    className="flex-1 px-4 py-2 bg-muted text-foreground rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || newPin.length < 4 || newPin !== confirmNewPin}
                    className="flex-1 px-4 py-2 bg-violet-500 text-white rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-reset-button"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Set New PIN
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
